import * as yup from 'yup';
import { InspectionShotKey } from '@carvia/ros-client-types';
import { uniq, uniqBy } from 'lodash';

export const yieldManagementOtherSchema = yup.object({
  youngDriverUntilAge: yup.number().min(0).required(),
  fuelingPricePerLiter: yup.number().min(0).required(),
  chargingPricePerKwh: yup.number().min(0).required(),
  stationTransferDiscountModifier: yup.number().min(0).required(),
  stationTransferPriceLimitModifier: yup.number().min(0).required(),
  disabledInspectionShotKeys: yup.array(
    yup.mixed<InspectionShotKey>().oneOf(Object.values(InspectionShotKey)).required(),
  ),
  requireInspectionStationIds: yup.array(yup.string().uuid().required()).required(),
  minAges: yup
    .array(
      yup.object({
        netPurchasePriceLt: yup.number().min(0).required().nullable(),
        minAge: yup.number().min(0).required(),
      }),
    )
    .test(
      'no-duplicate-net-purchase-price-lt',
      'Every min age step must have a different "net purchase price smaller than"',
      (steps) => (steps?.length ?? '') === uniqBy(steps, 'netPurchasePriceLt').length,
    )
    .test(
      'must-have-one-net-purchase-price-lt-equal-to-null',
      'There must be one empty (open ended) "net purchase price smaller than"',
      (step) => step?.some(({ netPurchasePriceLt }) => netPurchasePriceLt === null),
    ),
  deposits: yup
    .array(
      yup.object({
        netPurchasePriceLt: yup.number().min(0).required().nullable(),
        deposit: yup.number().min(0).required(),
      }),
    )
    .test(
      'no-duplicate-net-purchase-price-lt',
      'Every deposit step must have a different "net purchase price smaller than"',
      (steps) => (steps?.length ?? '') === uniqBy(steps, 'netPurchasePriceLt').length,
    )
    .test(
      'must-have-one-net-purchase-price-lt-equal-to-null',
      'There must be one empty (open ended) "net purchase price smaller than"',
      (step) => step?.some(({ netPurchasePriceLt }) => netPurchasePriceLt === null),
    ),
  debitBookingCategories: yup.array(
    yup.object({
      categories: yup.array(yup.string()).required(),
      netPurchasePriceLt: yup.number().required().min(0),
    }),
  ),
  commissionPercentageOptions: yup.array(
    yup.object({
      value: yup.number().min(0).max(100).required(),
      label: yup.string().required(),
    }),
  ),
  discountPercentageOptions: yup.array(
    yup.object({
      value: yup.number().min(0).max(100).required(),
      label: yup.string().required(),
    }),
  ),
  cleaningFees: yup.array(
    yup.object({
      id: yup.string().required(),
      name: yup.object({
        de: yup.string().required(),
        en: yup.string().required(),
      }),
      price: yup.number().min(0).required(),
      photoRequired: yup.boolean().required(),
      guidelines: yup
        .object({
          de: yup.string().nullable().optional(),
          en: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    }),
  ),
  infringementFees: yup
    .array(
      yup.object({
        amount: yup.number().min(0).required(),
        countries: yup.array(yup.string().length(2).uppercase()).nullable(),
      }),
    )
    .min(1)
    .test(
      'at-least-one-default',
      'At least one default fee without countries is required',
      (entries) => {
        const defaults =
          entries?.filter((entry) => !entry.countries || entry.countries.length === 0).length ?? 0;
        return defaults >= 1;
      },
    )
    .test('only-one-default', 'Only one default fee without countries is allowed', (entries) => {
      const defaults =
        entries?.filter((entry) => !entry.countries || entry.countries.length === 0).length ?? 0;
      return defaults <= 1;
    })
    .test('no-duplicate-countries', 'Countries can only be assigned to one fee', (entries) => {
      const countries = entries?.flatMap((entry) => entry.countries ?? []) ?? [];
      return countries.length === uniq(countries).length;
    }),
  crossborderCountryCodes: yup
    .array(yup.string().length(2).uppercase())
    .min(1, 'At least one country is required')
    .test('no-duplicate-codes', 'Each country can only be added once', (codes) => {
      if (!codes) return true;
      return codes.length === uniq(codes).length;
    }),
  crossborderCountryCustomLabels: yup
    .array(
      yup.object({
        code: yup.string().length(2).uppercase().required(),
        labels: yup.object({
          de: yup.string().required(),
          en: yup.string().required(),
          es: yup.string().optional(),
        }),
      }),
    )
    .test('codes-in-pool', 'Custom label country must be selected in the pool', function (entries) {
      const pool = (this.parent.crossborderCountryCodes as string[] | undefined) ?? [];
      return entries?.every((entry) => pool.includes(entry.code)) ?? true;
    })
    .test('no-duplicate-custom-labels', 'Each country can only be named once', (entries) => {
      const codes = entries?.map((entry) => entry.code) ?? [];
      return codes.length === uniq(codes).length;
    }),
  vatRates: yup
    .array(
      yup.object({
        country: yup.string().length(2).uppercase().required(),
        taxPercent: yup.number().min(0).max(100).required(),
        validFrom: yup.date().nullable(),
      }),
    )
    .test(
      'no-duplicate-country-date',
      'Each country and valid-from date combination can only be used once',
      (entries) => {
        const keys =
          entries?.map((entry) => {
            const day =
              entry.validFrom == null ? '' : new Date(entry.validFrom).toISOString().slice(0, 10);
            return `${entry.country}|${day}`;
          }) ?? [];
        return keys.length === uniq(keys).length;
      },
    ),
});
